import React, { useState, useEffect, useRef } from 'react';
import { _T, C } from '../../shared/theme';
import { s_PB, s_GB, s_GC, s_TI } from '../../shared/styles';
import Avatar from '../../components/ui/Avatar';
import { EXERCISES } from '../exercises/constants';
import { notifyUser } from '../../shared/dbService';
import GroupSessionScreen from './GroupSessionScreen';
import CompetitionsTab from './CompetitionsTab';

const BASE = process.env.REACT_APP_API_URL || '/api';

function GroupDetailScreen({ groupId, currentUserId, userName, lang, onBack, onNav }) {
  const nl = lang === 'nl';
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('schedule');
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState('');
  const [session, setSession] = useState(null);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleEx, setScheduleEx] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [dbUsers, setDbUsers] = useState([]);
  const [inviteQuery, setInviteQuery] = useState('');
  const [showEdit, setShowEdit] = useState(false);
  const [editData, setEditData] = useState({});
  const pollRef = useRef(null);
  const lastMsgId = useRef(0);
  const chatEndRef = useRef(null);

  const isAdmin = group?.members?.some(m => m.userId === currentUserId && m.role === 'admin' && m.status === 'accepted');
  const isMember = group?.members?.some(m => m.userId === currentUserId && m.status === 'accepted');
  const myStatus = group?.members?.find(m => m.userId === currentUserId)?.status;

  const loadGroup = async () => {
    try {
      const r = await fetch(`${BASE}/groups/${groupId}?uid=${encodeURIComponent(currentUserId)}`);
      if (r.ok) {
        const d = await r.json();
        setGroup(d);
        if (d.messages && d.messages.length) {
          setMessages(d.messages);
          lastMsgId.current = Math.max(...d.messages.map(m => m.id));
        }
      }
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => {
    loadGroup();
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${BASE}/groups/${groupId}/messages?since=${lastMsgId.current}`);
        if (r.ok) {
          const newMsgs = await r.json();
          if (newMsgs.length) {
            setMessages(prev => [...prev, ...newMsgs]);
            lastMsgId.current = Math.max(...newMsgs.map(m => m.id));
          }
        }
      } catch {}
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [groupId]);

  useEffect(() => {
    if (tab === 'chat') chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, tab]);

  useEffect(() => {
    if (showInvite && !dbUsers.length) {
      fetch(`${BASE}/users?exclude=${encodeURIComponent(currentUserId)}`)
        .then(r => r.ok ? r.json() : [])
        .then(data => { if (Array.isArray(data)) setDbUsers(data); })
        .catch(() => {});
    }
  }, [showInvite]);

  const sendMessage = async () => {
    if (!msgText.trim()) return;
    const text = msgText.trim();
    setMsgText('');
    const tempMsg = { id: Date.now(), userId: currentUserId, userName, text, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) };
    setMessages(prev => [...prev, tempMsg]);
    try {
      const r = await fetch(`${BASE}/groups/${groupId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUserId, userName, text }),
      });
      if (r.ok) { const d = await r.json(); if (d.id && d.id > lastMsgId.current) lastMsgId.current = d.id; }
    } catch {}
  };

  const joinGroup = async () => {
    await fetch(`${BASE}/groups/${groupId}/members`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid: currentUserId }),
    });
    // Notify admins of join request
    group?.members?.filter(m => m.role === 'admin' && m.status === 'accepted').forEach(m => {
      notifyUser(m.userId, 'group_join_request', userName,
        `${userName} ${nl ? 'vraagt toegang tot' : 'requests to join'} ${group.name}`);
    });
    loadGroup();
  };

  const acceptInvite = async () => {
    await fetch(`${BASE}/groups/${groupId}/members/${currentUserId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid: currentUserId, status: 'accepted' }),
    });
    loadGroup();
  };

  const scheduleExercise = async () => {
    if (!scheduleEx || !scheduleDate) return;
    const r = await fetch(`${BASE}/groups/${groupId}/schedule`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ exId: scheduleEx, scheduledAt: scheduleDate }),
    });
    if (r.ok) {
      const exDef = EXERCISES.find(e => e.id === scheduleEx);
      const exName = nl ? (exDef?.label || scheduleEx) : (exDef?.labelEN || scheduleEx);
      group?.members?.filter(m => m.status === 'accepted' && m.userId !== currentUserId).forEach(m => {
        notifyUser(m.userId, 'group_exercise_scheduled', userName,
          `${nl ? 'Oefening ingepland in' : 'Exercise scheduled in'} ${group.name}: ${exName}`);
      });
      setScheduleEx(''); setScheduleDate(''); setShowScheduleForm(false);
      loadGroup();
    }
  };

  const removeSchedule = async (eid) => {
    await fetch(`${BASE}/groups/${groupId}/schedule/${eid}`, { method: 'DELETE' });
    loadGroup();
  };

  const startSession = async (se) => {
    await fetch(`${BASE}/groups/${groupId}/schedule/${se.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'active', startedBy: currentUserId }),
    });
    const exDef = EXERCISES.find(e => e.id === se.exId);
    const exName = nl ? (exDef?.label || se.exId) : (exDef?.labelEN || se.exId);
    group?.members?.filter(m => m.status === 'accepted' && m.userId !== currentUserId).forEach(m => {
      notifyUser(m.userId, 'group_session_started', userName,
        `${nl ? 'Groepsoefening gestart in' : 'Group session started in'} ${group.name}: ${exName}`);
    });
    setSession({ exerciseId: se.exId, scheduleId: se.id });
  };

  const inviteUser = async (toId) => {
    await fetch(`${BASE}/groups/${groupId}/members`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid: currentUserId, inviteUid: toId }),
    });
    notifyUser(toId, 'group_invite', userName,
      `${nl ? 'Je bent uitgenodigd voor' : 'You are invited to'} ${group?.name}`,
      { groupId, groupName: group?.name, groupIcon: group?.icon, groupColor: group?.color, inviterUserId: currentUserId }
    );
    setDbUsers(prev => prev.filter(u => u.id !== toId));
  };

  const removeMember = async (mUid) => {
    await fetch(`${BASE}/groups/${groupId}/members/${mUid}`, { method: 'DELETE' });
    loadGroup();
  };

  const promoteAdmin = async (mUid) => {
    await fetch(`${BASE}/groups/${groupId}/members/${mUid}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid: currentUserId, role: 'admin' }),
    });
    loadGroup();
  };

  const acceptMember = async (mUid) => {
    await fetch(`${BASE}/groups/${groupId}/members/${mUid}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid: currentUserId, status: 'accepted' }),
    });
    notifyUser(mUid, 'group_joined', userName,
      `${nl ? 'Je bent toegelaten tot' : 'You have been accepted into'} ${group?.name}`);
    loadGroup();
  };

  const saveEdit = async () => {
    await fetch(`${BASE}/groups/${groupId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...editData, uid: currentUserId }),
    });
    setShowEdit(false); loadGroup();
  };

  const deleteGroup = async () => {
    if (!window.confirm(nl ? 'Groep definitief verwijderen?' : 'Delete this group permanently?')) return;
    await fetch(`${BASE}/groups/${groupId}?uid=${currentUserId}`, { method: 'DELETE' });
    onBack();
  };

  if (session) {
    return (
      <GroupSessionScreen
        groupId={groupId} exerciseId={session.exerciseId} scheduleId={session.scheduleId}
        currentUserId={currentUserId} userName={userName} lang={lang}
        groupName={group?.name || ''} onBack={() => { setSession(null); loadGroup(); }}
      />
    );
  }

  if (loading) return <div style={{ textAlign: 'center', color: C.silver, padding: 40 }}>...</div>;
  if (!group) return <div style={{ color: C.silver, textAlign: 'center', padding: 40 }}>{nl ? 'Niet gevonden' : 'Not found'}</div>;

  const acceptedMembers = (group.members || []).filter(m => m.status === 'accepted');
  const pendingMembers  = (group.members || []).filter(m => m.status === 'pending');
  const nonMembers = dbUsers.filter(u => !(group.members || []).some(m => m.userId === u.id));

  return (
    <div className="slideIn">
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13, marginBottom: 12 }}>
        ← {nl ? 'Terug' : 'Back'}
      </button>

      {/* Header */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 16 }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: `${group.color}18`, border: `2px solid ${group.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>
          {group.icon}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: C.white, fontSize: 17, fontWeight: 700 }}>{group.name}</div>
          {group.description && <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.5 }}>{group.description}</div>}
          <div style={{ color: C.silver, fontSize: 11, marginTop: 2 }}>👥 {acceptedMembers.length} {nl ? 'leden' : 'members'}</div>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => { setEditData({ name: group.name, description: group.description || '', icon: group.icon, color: group.color }); setShowEdit(true); }}
              style={{ background: 'none', border: `1px solid ${_T.accent}44`, borderRadius: 8, padding: '6px 10px', color: _T.accent, cursor: 'pointer', fontSize: 12 }}>✏️</button>
            <button onClick={deleteGroup}
              style={{ background: 'none', border: '1px solid #ff444444', borderRadius: 8, padding: '6px 10px', color: '#ff4444', cursor: 'pointer', fontSize: 12 }}>🗑️</button>
          </div>
        )}
      </div>

      {/* Edit form */}
      {showEdit && (
        <div style={s_GC({ marginBottom: 14 })}>
          <input value={editData.name || ''} onChange={e => setEditData({ ...editData, name: e.target.value })}
            style={s_TI({ marginBottom: 8 })} placeholder={nl ? 'Naam' : 'Name'} />
          <textarea value={editData.description || ''} onChange={e => setEditData({ ...editData, description: e.target.value })}
            style={s_TI({ minHeight: 60, resize: 'none', marginBottom: 8 })} placeholder={nl ? 'Beschrijving' : 'Description'} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={s_PB({ flex: 1 })} onClick={saveEdit}>{nl ? 'Opslaan' : 'Save'}</button>
            <button style={s_GB({ flex: 1 })} onClick={() => setShowEdit(false)}>{nl ? 'Annuleren' : 'Cancel'}</button>
          </div>
        </div>
      )}

      {/* Join / accept / pending */}
      {!isMember && myStatus !== 'pending' && myStatus !== 'invited' && (
        <button style={s_PB({ width: '100%', marginBottom: 12 })} onClick={joinGroup}>
          {group.isPublic ? (nl ? 'Deelnemen' : 'Join') : (nl ? 'Toegang aanvragen' : 'Request to join')}
        </button>
      )}
      {myStatus === 'invited' && (
        <button style={s_PB({ width: '100%', marginBottom: 12 })} onClick={acceptInvite}>
          {nl ? 'Uitnodiging accepteren' : 'Accept invitation'}
        </button>
      )}
      {myStatus === 'pending' && (
        <div style={{ textAlign: 'center', color: C.silver, fontSize: 13, marginBottom: 12 }}>
          ⏳ {nl ? 'Aanvraag ingediend — wacht op goedkeuring' : 'Request pending — awaiting approval'}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', background: _T.bg4, borderRadius: 12, padding: 3, marginBottom: 14, gap: 2 }}>
        {['schedule','members','chat','compete'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ flex: 1, background: tab === t ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : 'transparent', border: 'none', borderRadius: 9, padding: '8px 4px', cursor: 'pointer', color: tab === t ? '#0a1220' : C.silver, fontSize: 11, fontWeight: tab === t ? 700 : 400 }}>
            {t === 'schedule' ? (nl ? '📅 Rooster' : '📅 Schedule') : t === 'members' ? (nl ? '👥 Leden' : '👥 Members') : t === 'chat' ? '💬 Chat' : '⚔️'}
          </button>
        ))}
      </div>

      {/* ── Schedule tab ── */}
      {tab === 'schedule' && (
        <div>
          {isAdmin && (
            <button style={s_GB({ width: '100%', marginBottom: 10 })} onClick={() => setShowScheduleForm(!showScheduleForm)}>
              {showScheduleForm ? '✕' : '+ ' + (nl ? 'Oefening inplannen' : 'Schedule exercise')}
            </button>
          )}
          {showScheduleForm && (
            <div style={s_GC({ marginBottom: 12 })}>
              <select value={scheduleEx} onChange={e => setScheduleEx(e.target.value)}
                style={{ ...s_TI({ marginBottom: 8 }), appearance: 'none' }}>
                <option value="">{nl ? 'Kies oefening...' : 'Choose exercise...'}</option>
                {EXERCISES.map(ex => (
                  <option key={ex.id} value={ex.id}>{ex.icon} {nl ? ex.label : ex.labelEN}</option>
                ))}
              </select>
              <input type="datetime-local" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)}
                style={s_TI({ marginBottom: 8 })} />
              <button style={s_PB({ width: '100%', opacity: scheduleEx && scheduleDate ? 1 : 0.4 })}
                onClick={scheduleExercise} disabled={!scheduleEx || !scheduleDate}>
                {nl ? 'Inplannen' : 'Schedule'}
              </button>
            </div>
          )}
          {(group.schedule || []).length === 0 && (
            <div style={{ textAlign: 'center', color: C.silver, fontSize: 13, padding: 24 }}>
              {nl ? 'Geen geplande oefeningen' : 'No scheduled exercises'}
            </div>
          )}
          {(group.schedule || []).map(se => {
            const exDef = EXERCISES.find(e => e.id === se.exId);
            const when = new Date(se.scheduledAt);
            const isActive = se.status === 'active';
            const isDone = se.status === 'done';
            return (
              <div key={se.id} style={s_GC({ padding: 14, marginBottom: 8 })}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <div style={{ fontSize: 28 }}>{exDef?.icon || '🧩'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: C.white, fontSize: 14, fontWeight: 600 }}>{nl ? (exDef?.label || se.exId) : (exDef?.labelEN || se.exId)}</div>
                    <div style={{ color: C.silver, fontSize: 11 }}>
                      {when.toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} {when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div style={{ fontSize: 10, color: isDone ? '#2A9E82' : isActive ? _T.accent : C.silver, fontWeight: 700, marginTop: 2 }}>
                      {isDone ? (nl ? '✅ KLAAR' : '✅ DONE') : isActive ? (nl ? '🟢 ACTIEF' : '🟢 ACTIVE') : (nl ? '📅 GEPLAND' : '📅 PLANNED')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                    {isMember && !isDone && (
                      <button style={s_PB({ padding: '6px 12px', fontSize: 11 })} onClick={() => startSession(se)}>
                        {isActive ? (nl ? 'Deelnemen' : 'Join') : (nl ? 'Starten' : 'Start')}
                      </button>
                    )}
                    {isAdmin && !isDone && (
                      <button onClick={() => removeSchedule(se.id)} style={{ background: 'none', border: 'none', color: '#ff4444', cursor: 'pointer', fontSize: 13 }}>✕</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Members tab ── */}
      {tab === 'members' && (
        <div>
          {isAdmin && (
            <button style={s_GB({ width: '100%', marginBottom: 10 })} onClick={() => { setShowInvite(v => !v); setInviteQuery(''); }}>
              {showInvite ? '✕' : '+ ' + (nl ? 'Uitnodigen' : 'Invite user')}
            </button>
          )}
          {showInvite && (
            <div style={s_GC({ marginBottom: 12 })}>
              <div style={{ color: C.silver, fontSize: 12, marginBottom: 8 }}>{nl ? 'Kies gebruiker om uit te nodigen:' : 'Choose user to invite:'}</div>
              <input
                style={s_TI({ marginBottom: 8 })}
                placeholder={nl ? 'Zoek op naam…' : 'Search by name…'}
                value={inviteQuery}
                onChange={e => setInviteQuery(e.target.value)}
              />
              {nonMembers.filter(u => !inviteQuery.trim() || (u.name || '').toLowerCase().includes(inviteQuery.toLowerCase())).slice(0, 20).map(u => (
                <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${_T.cardBorderBot}` }}>
                  <Avatar user={u} size={32} />
                  <div style={{ flex: 1, color: C.white, fontSize: 13 }}>{u.name}</div>
                  <button style={s_PB({ padding: '5px 12px', fontSize: 11 })} onClick={() => inviteUser(u.id)}>
                    {nl ? 'Uitnodigen' : 'Invite'}
                  </button>
                </div>
              ))}
              {nonMembers.filter(u => !inviteQuery.trim() || (u.name || '').toLowerCase().includes(inviteQuery.toLowerCase())).length === 0 && (
                <div style={{ color: C.silver, fontSize: 12, textAlign: 'center', padding: 8 }}>{nl ? 'Geen gebruikers gevonden' : 'No users found'}</div>
              )}
            </div>
          )}

          {isAdmin && pendingMembers.length > 0 && (
            <div style={s_GC({ marginBottom: 12 })}>
              <div style={{ fontSize: 11, color: _T.accent, letterSpacing: 2, fontWeight: 700, marginBottom: 8 }}>{nl ? 'AANVRAGEN' : 'REQUESTS'}</div>
              {pendingMembers.map(m => (
                <div key={m.userId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${_T.cardBorderBot}` }}>
                  <Avatar user={{ name: m.name, picture: m.picture, color: m.color }} size={32} />
                  <div style={{ flex: 1, color: C.white, fontSize: 13 }}>{m.name}</div>
                  <button style={s_PB({ padding: '5px 12px', fontSize: 11 })} onClick={() => acceptMember(m.userId)}>✓</button>
                  <button onClick={() => removeMember(m.userId)} style={{ background: 'none', border: 'none', color: '#ff4444', cursor: 'pointer', fontSize: 15 }}>✕</button>
                </div>
              ))}
            </div>
          )}

          <div style={s_GC()}>
            <div style={{ fontSize: 11, color: _T.accent, letterSpacing: 2, fontWeight: 700, marginBottom: 10 }}>{nl ? 'LEDEN' : 'MEMBERS'}</div>
            {acceptedMembers.map((m, i) => (
              <div key={m.userId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < acceptedMembers.length - 1 ? `1px solid ${_T.cardBorderBot}` : 'none' }}>
                <Avatar user={{ name: m.name, picture: m.picture, color: m.color }} size={34} />
                <div style={{ flex: 1 }}>
                  <div style={{ color: C.white, fontSize: 13 }}>{m.name}</div>
                  {m.role === 'admin' && <div style={{ fontSize: 9, color: _T.accent, fontWeight: 700 }}>👑 ADMIN</div>}
                </div>
                {isAdmin && m.userId !== currentUserId && (
                  <div style={{ display: 'flex', gap: 4 }}>
                    {m.role !== 'admin' && (
                      <button onClick={() => promoteAdmin(m.userId)} style={{ background: 'none', border: `1px solid ${_T.accent}44`, borderRadius: 6, padding: '4px 8px', color: _T.accent, cursor: 'pointer', fontSize: 10 }}>👑</button>
                    )}
                    <button onClick={() => removeMember(m.userId)} style={{ background: 'none', border: 'none', color: '#ff4444', cursor: 'pointer', fontSize: 15 }}>✕</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Chat tab ── */}
      {tab === 'chat' && (
        <div>
          <div style={{ ...s_GC({ padding: 12 }), maxHeight: 380, overflowY: 'auto' }}>
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', color: C.silver, fontSize: 13, padding: 16 }}>{nl ? 'Nog geen berichten' : 'No messages yet'}</div>
            )}
            {messages.map((m, i) => {
              const isMe = m.userId === currentUserId;
              return (
                <div key={m.id || i} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                  <div style={{ maxWidth: '75%', background: isMe ? `${_T.accent}22` : _T.bg4, border: `1px solid ${isMe ? _T.accent + '44' : _T.cardBorderBot}`, borderRadius: 12, padding: '8px 12px' }}>
                    {!isMe && <div style={{ fontSize: 10, color: _T.accent, fontWeight: 700, marginBottom: 3 }}>{m.userName}</div>}
                    <div style={{ color: C.white, fontSize: 13, lineHeight: 1.5 }}>{m.text}</div>
                    <div style={{ fontSize: 10, color: C.silver, marginTop: 3, textAlign: isMe ? 'right' : 'left' }}>
                      {typeof m.time === 'string' && m.time.includes('T')
                        ? new Date(m.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        : m.time}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={chatEndRef} />
          </div>
          {isMember && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <input value={msgText} onChange={e => setMsgText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendMessage()}
                placeholder={nl ? 'Typ een bericht...' : 'Type a message...'}
                style={s_TI({ flex: 1 })} />
              <button style={s_PB({ padding: '12px 16px' })} onClick={sendMessage}>→</button>
            </div>
          )}
        </div>
      )}

      {/* ── Competitions tab ── */}
      {tab === 'compete' && (
        <CompetitionsTab
          groupId={groupId}
          currentUserId={currentUserId}
          userName={userName}
          isAdmin={isAdmin}
          lang={lang}
          onPlayGame={gameId => { if (onNav) onNav(3); }}
        />
      )}
    </div>
  );
}

export default GroupDetailScreen;
